import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import { useDispatch, useSelector } from "react-redux";
import { setUserInfo } from "../../redux/hejalSlice";
import toast from "react-hot-toast";

const DEFAULT_PROFILE_IMAGE = "https://via.placeholder.com/150";

const UserVerify = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // More robust selector with multiple fallback checks
  const user = useSelector((state) => {
    const userInfo = state.hejal?.userInfo;
    return userInfo && typeof userInfo === 'object' ? userInfo : {};
  });

  const [userData, setUserData] = useState({
    fullName: user.fullName || "",
    phoneNumber: "",
    profileImage: user.profileImage || DEFAULT_PROFILE_IMAGE,
    accountType: user.accountType || "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Additional null and type checking
    if (user && typeof user === 'object' && Object.keys(user).length > 0) {
      setUserData(prevData => ({
        ...prevData,
        fullName: user.fullName || prevData.fullName,
        profileImage: user.profileImage || DEFAULT_PROFILE_IMAGE,
      }));
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Store the file for upload
      setSelectedFile(file);

      // Create a preview of the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setUserData(prev => ({
          ...prev,
          profileImage: reader.result || DEFAULT_PROFILE_IMAGE
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    if (!userData.phoneNumber?.trim()) {
      toast.error("Phone number is required");
      return;
    }
    if (!userData.accountType) {
      toast.error("Choose an account type");
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("fullName", userData.fullName || "");
    formData.append("phoneNumber", userData.phoneNumber);
    formData.append("accountType", userData.accountType || "");

    try {
      // If a new file is selected, upload it
      if (selectedFile) {
        formData.append("profileImage", selectedFile, selectedFile.name);
      }

      const response = await ApiService({
        method: "PUT",
        endpoint: apiPaths.UpdateProfile,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Ensure response.data is an object before dispatching
      const updatedUserInfo = response.data && typeof response.data === 'object'
        ? response.data
        : {};

      dispatch(setUserInfo(updatedUserInfo));
      toast.success("Profile updated successfully!");
      if (userData.accountType.toLowerCase() === "wholesaler") {
        navigate("/wholesaler");
        return;
      }
      else {
        navigate("/");
      }
    } catch (error) {
      console.error("Profile update failed:", error);
      toast.error(error?.response?.data?.message || "Error updating profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen flex items-center justify-center bg-gray-100">
      <div className="p-8 bg-white rounded-xl shadow-lg w-96 flex flex-col items-center">
        <h2 className="text-2xl font-bold mb-4 text-gray-700">
          Complete Your Profile
        </h2>
        <div className="w-24 h-24 mb-4 rounded-full border-4 border-blue-500 overflow-hidden">
          <img
            src={userData.profileImage || DEFAULT_PROFILE_IMAGE}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="mb-4 text-sm"
        />
        <form onSubmit={handleUpdateProfile} className="flex flex-col gap-4 w-full">
          <input
            type="text"
            name="fullName"
            value={userData.fullName}
            onChange={handleChange}
            className="border p-3 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Full Name"
          />
          <input
            type="text"
            name="phoneNumber"
            value={userData.phoneNumber}
            onChange={handleChange}
            className="border p-3 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Phone Number"
          />
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Account Type
            </label>
            <div className="flex space-x-4">
              {["retailer"].map((type) => (
                <label
                  key={type}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="accountType"
                    value={type.toLowerCase()}
                    checked={userData.accountType === type.toLowerCase()}
                    onChange={handleChange}
                    className="form-radio text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{type}</span>
                </label>
              ))}
            </div>
          </div>
          <button
            type="submit"
            className="bg-secondColor text-white p-3 rounded-lg font-bold shadow-md hover:shadow-lg transition-all duration-300"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update Profile"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserVerify;
